import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { WindowToggle } from "@/components/window-toggle";
import { Table, Th, Td, fmtUsd } from "@/components/data-table";
import { ChainBadge } from "@/components/chain-badge";
import { PackHoverPreview } from "@/components/pack-hover-preview";
import { ScrambleText } from "@/components/scramble-text";
import type { WindowLabel } from "@/lib/types";

interface Row {
  chain: "base" | "apechain";
  pack: string;
  name: string;
  priceUsd: number;
  pulls: number;
  volumeUsd: number;
  payoutUsd: number;
  realizedEvPerPullUsd: number;
}

export default function CollectionsPage() {
  const [window, setWindow] = useState<WindowLabel>("7d");
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["collections", window],
    queryFn: async () =>
      (await (await fetch(`/api/trending-collections?window=${window}`)).json()) as {
        rows: Row[];
      },
  });

  return (
    <div className="p-3 md:p-4 space-y-3 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold uppercase tracking-[0.2em] neon-text cursor">
            <ScrambleText text="trending collections" duration={900} revealEach={45} />
          </h1>
          <p className="text-[11px] text-muted max-w-3xl font-mono">
            <span className="text-accent2">›</span> packs ranked by pull count in window ·
            volume = pulls × price
          </p>
        </div>
        <div className="flex items-center gap-2">
          <WindowToggle value={window} onChange={setWindow} />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded border border-border bg-panel/60 px-3 py-1 text-xs font-mono uppercase tracking-wider hover:border-accent hover:text-accent hover:shadow-glow transition-all disabled:opacity-50"
          >
            {isFetching ? "› syncing…" : "[refresh]"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded border border-border bg-panel/60 p-6 text-sm text-muted font-mono">
          <span className="text-accent2">›</span> scanning on-chain
          <span className="animate-blink text-accent ml-1">_</span>
        </div>
      ) : (
        <>
        {/* Mobile: stacked cards, one per pack. */}
        <div className="md:hidden space-y-2">
          {(data?.rows ?? []).map((r, i) => (
            <div
              key={r.pack}
              className="rounded-md border border-border bg-panel/70 p-3 brackets animate-fadeIn"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-muted font-mono mb-0.5">
                    #{String(i + 1).padStart(2, "0")}
                  </div>
                  <PackHoverPreview
                    chainId={r.chain === "base" ? 8453 : 33139}
                    machineAddress={r.pack}
                  >
                    <div className="cursor-pointer">
                      <div className="font-medium text-text hover:text-accent transition-colors truncate">
                        {r.name}
                      </div>
                      <div className="text-[10px] text-muted font-mono">
                        {r.pack.slice(0, 6)}…{r.pack.slice(-4)}
                      </div>
                    </div>
                  </PackHoverPreview>
                </div>
                <ChainBadge chain={r.chain} />
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
                <div className="flex justify-between">
                  <span className="text-muted">Price</span>
                  <span className="tabular-nums">{fmtUsd(r.priceUsd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Pulls</span>
                  <span className="tabular-nums">{r.pulls}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Volume</span>
                  <span className="tabular-nums">{fmtUsd(r.volumeUsd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Payout</span>
                  <span className="tabular-nums">{fmtUsd(r.payoutUsd)}</span>
                </div>
                <div className="col-span-2 flex justify-between pt-1 border-t border-border/60">
                  <span className="text-muted">Realized EV/pull</span>
                  <span
                    className={
                      "tabular-nums font-bold " +
                      (r.realizedEvPerPullUsd > 0
                        ? "text-success"
                        : r.realizedEvPerPullUsd < 0
                        ? "text-danger"
                        : "")
                    }
                  >
                    {fmtUsd(r.realizedEvPerPullUsd, { sign: true })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Pack</Th>
              <Th>Chain</Th>
              <Th className="text-right">Price</Th>
              <Th className="text-right">Pulls</Th>
              <Th className="text-right">Volume</Th>
              <Th className="text-right">Payout</Th>
              <Th className="text-right">Realized EV/pull</Th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((r, i) => (
              <tr key={r.pack} className="row-glow transition-colors">
                <Td className="text-muted">{String(i + 1).padStart(2, "0")}</Td>
                <Td>
                  <PackHoverPreview
                    chainId={r.chain === "base" ? 8453 : 33139}
                    machineAddress={r.pack}
                  >
                    <div className="cursor-pointer">
                      <div className="font-medium text-text hover:text-accent transition-colors">
                        {r.name}
                      </div>
                      <div className="text-[10px] text-muted font-mono">
                        {r.pack.slice(0, 6)}…{r.pack.slice(-4)}
                      </div>
                    </div>
                  </PackHoverPreview>
                </Td>
                <Td>
                  <ChainBadge chain={r.chain} />
                </Td>
                <Td className="text-right">{fmtUsd(r.priceUsd)}</Td>
                <Td className="text-right">{r.pulls}</Td>
                <Td className="text-right">{fmtUsd(r.volumeUsd)}</Td>
                <Td className="text-right">{fmtUsd(r.payoutUsd)}</Td>
                <Td
                  className={
                    "text-right " +
                    (r.realizedEvPerPullUsd > 0
                      ? "text-success"
                      : r.realizedEvPerPullUsd < 0
                      ? "text-danger"
                      : "")
                  }
                >
                  {fmtUsd(r.realizedEvPerPullUsd, { sign: true })}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        </>
      )}
    </div>
  );
}
